import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Plus, Copy, RotateCcw, Pencil, Eye, EyeOff, Users, Loader2 } from "lucide-react";
import { RichTextEditor } from "@/components/common/RichTextEditor";
import { TemplateResources, TemplateSubmission } from "./TemplateResources";
import { useRoadmapImageSrc } from "@/components/roadmap/ProgramRoadmapImage";

function RoadmapImageAdmin({ programKey, orgParam }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/program-tasks/roadmap-images", { params: { organization_id: orgParam } })
      .then((r) => setInfo((r.data.items || []).find((i) => i.program_key === programKey) || null)).catch(() => setInfo(null));
  }, [programKey, orgParam]);
  useEffect(() => { load(); }, [load]);
  const src = useRoadmapImageSrc(info?.image_id, info?.default_url);
  if (!info) return null;
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); setBusy(true);
    try { await api.post(`/program-tasks/roadmap-images/${programKey}`, fd, { params: { organization_id: orgParam } }); toast.success("Roadmap image saved"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const revert = async () => { try { await api.delete(`/program-tasks/roadmap-images/${programKey}`, { params: { organization_id: orgParam } }); toast.success("Removed"); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4 flex items-center gap-4 flex-wrap" data-testid="tt-roadmap-image">
      {src ? <img src={src} alt={`${info.label} roadmap`} className="h-20 w-auto rounded border border-slate-200 object-contain" /> : <div className="h-20 w-32 rounded border border-dashed border-slate-300 flex items-center justify-center text-[11px] text-slate-400">No image</div>}
      <div className="flex-1 min-w-[200px]"><p className="text-sm font-semibold text-slate-800">{info.label} roadmap image</p>
        <p className="text-xs text-slate-500">Shown to candidates on My Journey → My Roadmap. {info.image_id ? (info.own ? "Custom image for this selection." : "Inherited image.") : info.default_url ? "Using the built-in default." : "No image yet — candidates see a short message."}</p></div>
      <label className={`inline-flex items-center rounded-md border border-slate-300 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50 ${busy ? "opacity-50 pointer-events-none" : ""}`} data-testid="tt-roadmap-upload">
        {info.image_id ? "Replace image" : "Upload image"}<input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="tt-roadmap-file" /></label>
      {info.own && <Button size="sm" variant="ghost" onClick={revert} data-testid="tt-roadmap-remove">Remove</Button>}
    </div>
  );
}

const ROLE_LABEL = { candidate: "Candidate", recruiter: "Recruiter", recruiting_manager: "Recruiting Mgr", support_staff: "Support Staff", group_admin: "Group Admin", agent: "Agent", sponsoring_agent: "Sponsoring Agent", supervising_agent: "Supervising Agent" };
const TRIGGER_LABEL = { on_enrollment: "On enrollment", on_phase: "On phase", after_task: "After task", manual: "Manual only" };
const OFFSET_TYPES = [["none", "No due date"], ["immediate", "Due immediately"], ["days", "In N days"], ["days_before_career", "N days before Career date"]];

// Light phase ordering mirror (min display_order per phase handles real ordering).
export default function TaskTemplates() {
  const { isSuper, isAdmin } = useAuth();
  const isMaster = isSuper || isAdmin;
  const [meta, setMeta] = useState(null);
  const [program, setProgram] = useState("retail");
  const [subtype, setSubtype] = useState("traditional");
  const [org, setOrg] = useState(isMaster ? "__master__" : "");
  const [state, setState] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);

  useEffect(() => {
    api.get("/program-tasks/meta").then((r) => {
      setMeta(r.data);
      if (!r.data.is_master_admin) setOrg((r.data.organizations?.[0]?.id) || "");
    }).catch((e) => toast.error(apiError(e)));
  }, []);

  const progMeta = useMemo(() => (meta?.programs || []).find((p) => p.program === program), [meta, program]);
  const hasSubtypes = (progMeta?.subtypes || []).length > 0;
  const orgParam = org === "__master__" ? "" : org;

  const load = useCallback(() => {
    if (!program) return;
    setLoading(true);
    api.get("/program-tasks/templates", { params: { program, subtype: hasSubtypes ? subtype : "", organization_id: orgParam, state } })
      .then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [program, subtype, hasSubtypes, orgParam, state]);
  useEffect(() => { load(); }, [load]);

  const orgSelected = org !== "__master__" && !!org;
  const items = data?.items || [];
  const phases = useMemo(() => {
    const groups = {};
    for (const it of items) {
      const ph = it.phase || "Program";
      if (!groups[ph]) groups[ph] = { name: ph, items: [], min: 9999 };
      groups[ph].items.push(it);
      groups[ph].min = Math.min(groups[ph].min, it.display_order ?? 9999);
    }
    return Object.values(groups).map((g) => ({ ...g, items: g.items.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)) }))
      .sort((a, b) => a.min - b.min);
  }, [items]);

  const saveItem = async (id, body) => {
    try {
      if (id) await api.patch(`/program-tasks/templates/${id}`, { ...body, organization_id: orgParam || null });
      else await api.post("/program-tasks/templates", { ...body, program, program_subtype: hasSubtypes ? subtype : null, organization_id: orgParam || null });
      toast.success(id ? "Saved" : "Task added"); setEditItem(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const quickPatch = async (it, body) => {
    try { await api.patch(`/program-tasks/templates/${it.id}`, { ...body, organization_id: orgParam || null }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const duplicate = async (it) => {
    try { await api.post(`/program-tasks/templates/${it.id}/duplicate`, { organization_id: orgParam || null }); toast.success("Duplicated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const restore = async (it) => {
    try { await api.post(`/program-tasks/templates/${it.id}/restore-default`, {}); toast.success("Restored to default"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const swap = async (list, i, dir) => {
    const j = i + dir; if (j < 0 || j >= list.length) return;
    try {
      await Promise.all([
        api.patch(`/program-tasks/templates/${list[i].id}`, { display_order: list[j].display_order, organization_id: orgParam || null }),
        api.patch(`/program-tasks/templates/${list[j].id}`, { display_order: list[i].display_order, organization_id: orgParam || null }),
      ]);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mt-4" data-testid="task-templates-tab">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Program">
          <Select value={program} onValueChange={(v) => { setProgram(v); const pm = (meta?.programs || []).find((p) => p.program === v); setSubtype((pm?.subtypes?.[0]?.value) || ""); }}>
            <SelectTrigger className="w-44" data-testid="tt-program-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{(meta?.programs || []).map((p) => <SelectItem key={p.program} value={p.program}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        {hasSubtypes && (
          <Field label="Subtype">
            <Select value={subtype} onValueChange={setSubtype}>
              <SelectTrigger className="w-40" data-testid="tt-subtype-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{progMeta.subtypes.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        )}
        <Field label="Organization">
          <Select value={org} onValueChange={setOrg}>
            <SelectTrigger className="w-60" data-testid="tt-org-select"><SelectValue placeholder="Select organization" /></SelectTrigger>
            <SelectContent className="bg-white max-h-72">
              {isMaster && <SelectItem value="__master__">Global Master (all orgs)</SelectItem>}
              {(meta?.organizations || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="State">
          <Select value={state} onValueChange={setState}>
            <SelectTrigger className="w-32" data-testid="tt-state-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{(meta?.states || ["all"]).map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All states" : s}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={() => setEditItem({ _new: true, phase: phases[0]?.name || "Program", required: true, active: true, candidate_visible: true, assigned_role: "candidate", state: "all", trigger: "on_phase", due_offset: { type: "none" } })} data-testid="tt-add-btn"><Plus className="w-4 h-4 mr-1" /> Add Task</Button>
          <Button onClick={() => setApplyOpen(true)} data-testid="tt-apply-open-btn" className="bg-[#006090] hover:bg-[#004d73]"><Users className="w-4 h-4 mr-1" /> Apply to Existing Enrollments</Button>
        </div>
      </div>

      <div className="mt-3 text-xs text-slate-500">
        {org === "__master__" ? "Editing the GLOBAL MASTER defaults (apply to every organization that hasn't overridden them)." :
          "Editing this organization — changes to inherited tasks create an organization override; org-only tasks are added here. Milestone gating is never affected."}
      </div>
      <RoadmapImageAdmin programKey={program === "retail" ? `retail_${subtype || "traditional"}` : program} orgParam={orgParam} />

      {loading || !data ? <p className="text-sm text-slate-400 mt-8">Loading…</p> : items.length === 0 ? (
        <p className="text-sm text-slate-400 mt-8">No template tasks for this selection.</p>
      ) : (
        <div className="mt-5 space-y-5">
          {phases.map((ph) => (
            <div key={ph.name} className="rounded-xl border border-slate-200 bg-white" data-testid={`tt-phase-${ph.name}`}>
              <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50 rounded-t-xl font-semibold text-sm text-slate-700">{ph.name} <span className="text-slate-400 font-normal">· {ph.items.length}</span></div>
              <div className="divide-y divide-slate-100">
                {ph.items.map((it, i) => (
                  <Row key={it.id} it={it} i={i} list={ph.items} orgSelected={orgSelected} canMaster={data.can_edit_master}
                    onSwap={swap} onEdit={() => setEditItem(it)} onDup={() => duplicate(it)} onRestore={() => restore(it)}
                    onToggleActive={(v) => quickPatch(it, { active: v })} onToggleVisible={(v) => quickPatch(it, { candidate_visible: v })} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {editItem && <EditDialog item={editItem} meta={meta} siblings={items.filter((x) => x.id !== editItem.id)} onClose={() => setEditItem(null)} onSave={(body) => saveItem(editItem._new ? null : editItem.id, body)} />}
      {applyOpen && <ApplyDialog program={program} subtype={hasSubtypes ? subtype : ""} orgParam={orgParam} onClose={() => { setApplyOpen(false); load(); }} />}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1"><label className="text-[11px] font-medium text-slate-500">{label}</label>{children}</div>
);

function Badge({ tone = "slate", children, testid }) {
  const tones = { slate: "bg-slate-100 text-slate-600", teal: "bg-teal-50 text-teal-700 border border-teal-200", blue: "bg-blue-50 text-blue-700 border border-blue-200", amber: "bg-amber-50 text-amber-700", green: "bg-emerald-50 text-emerald-700" };
  return <span data-testid={testid} className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${tones[tone]}`}>{children}</span>;
}

function Row({ it, i, list, orgSelected, canMaster, onSwap, onEdit, onDup, onRestore, onToggleActive, onToggleVisible }) {
  const isOverride = it.source === "override";
  const isOrgAdded = it.source === "org";
  const editableMaster = it.scope === "master" ? canMaster : true;
  return (
    <div className={`p-3 flex flex-wrap items-center gap-2.5 ${it.active === false ? "opacity-50" : ""}`} data-testid={`tt-row-${it.id}`}>
      <div className="flex flex-col">
        <button disabled={i <= 0} onClick={() => onSwap(list, i, -1)} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`tt-up-${it.id}`}><ArrowUp className="w-3.5 h-3.5" /></button>
        <button disabled={i >= list.length - 1} onClick={() => onSwap(list, i, 1)} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`tt-down-${it.id}`}><ArrowDown className="w-3.5 h-3.5" /></button>
      </div>
      <div className="flex-1 min-w-[220px]">
        <div className="text-sm font-medium text-slate-800">{it.title}</div>
        <div className="flex flex-wrap items-center gap-1.5 mt-1">
          {isOverride ? <Badge tone="teal" testid={`tt-badge-override-${it.id}`}>Org override</Badge> : isOrgAdded ? <Badge tone="blue" testid={`tt-badge-orgadded-${it.id}`}>Org-added</Badge> : <Badge tone="slate" testid={`tt-badge-inherited-${it.id}`}>Inherited</Badge>}
          <Badge tone={it.candidate_visible ? "green" : "slate"}>{it.candidate_visible ? "Candidate visible" : "Internal"}</Badge>
          <Badge tone={it.required ? "amber" : "slate"}>{it.required ? "Required" : "Optional"}</Badge>
          <Badge>{ROLE_LABEL[it.assigned_role] || it.assigned_role}</Badge>
          {it.state && it.state !== "all" && <Badge tone="blue">{it.state}</Badge>}
          <Badge>{TRIGGER_LABEL[it.trigger] || it.trigger}</Badge>
        </div>
      </div>
      <label className="flex items-center gap-1 text-[11px] text-slate-600"><Switch checked={!!it.candidate_visible} onCheckedChange={onToggleVisible} data-testid={`tt-vis-${it.id}`} />{it.candidate_visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}</label>
      <label className="flex items-center gap-1 text-[11px] text-slate-600"><Switch checked={it.active !== false} onCheckedChange={onToggleActive} data-testid={`tt-active-${it.id}`} /> Active</label>
      {editableMaster && <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`tt-edit-${it.id}`}><Pencil className="w-3.5 h-3.5" /></Button>}
      <Button size="sm" variant="ghost" onClick={onDup} data-testid={`tt-dup-${it.id}`}><Copy className="w-3.5 h-3.5" /></Button>
      {orgSelected && isOverride && <Button size="sm" variant="ghost" onClick={onRestore} title="Restore default" data-testid={`tt-restore-${it.id}`}><RotateCcw className="w-3.5 h-3.5 text-amber-600" /></Button>}
    </div>
  );
}

function EditDialog({ item, meta, siblings = [], onClose, onSave }) {
  const [f, setF] = useState({
    title: item.title || "", candidate_title: item.candidate_title || "", why_it_matters: item.why_it_matters || "", phase: item.phase || "Program", assigned_role: item.assigned_role || "candidate",
    state: item.state || "all", required: item.required !== false, classification: item.classification || "", candidate_visible: !!item.candidate_visible,
    active: item.active !== false, trigger: item.trigger || "on_phase", after_item_key: item.after_item_key || "",
    offset_type: item.due_offset?.type || "none", offset_days: item.due_offset?.days || 0,
    candidate_instructions: item.candidate_instructions || "", internal_notes: item.internal_notes || "",
    resources: item.resources || [], submission: item.submission || { required: false },
  });
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = () => {
    if (!f.title.trim()) return toast.error("Title is required");
    if (f.trigger === "after_task" && !f.after_item_key) return toast.error("Choose the prerequisite task for an After task trigger");
    const due_offset = f.offset_type === "days" || f.offset_type === "days_before_career" ? { type: f.offset_type, days: Number(f.offset_days) || 0 } : { type: f.offset_type };
    onSave({ title: f.title.trim(), candidate_title: f.candidate_title.trim() || null, why_it_matters: f.why_it_matters.trim() || null, phase: f.phase, assigned_role: f.assigned_role, state: f.state, required: f.required, classification: f.classification || null, candidate_visible: f.candidate_visible, active: f.active, trigger: f.trigger, after_item_key: f.trigger === "after_task" ? f.after_item_key : null, due_offset, candidate_instructions: f.candidate_instructions || null, internal_notes: f.internal_notes || null, resources: f.resources, submission: f.submission });
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="tt-edit-dialog">
        <DialogHeader><DialogTitle>{item._new ? "Add Task Template" : "Edit Task Template"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs font-medium text-slate-500">Title (internal)</label><Input value={f.title} onChange={(e) => up("title", e.target.value)} data-testid="tt-edit-title" /></div>
          <div><label className="text-xs font-medium text-slate-500">Candidate display title (optional — falls back to Title)</label><Input value={f.candidate_title} onChange={(e) => up("candidate_title", e.target.value)} placeholder="e.g. Prepare for Your Agency Appointment" data-testid="tt-edit-candidate-title" /></div>
          <div><label className="text-xs font-medium text-slate-500">Why this matters (shown to candidate, optional)</label><Input value={f.why_it_matters} onChange={(e) => up("why_it_matters", e.target.value)} placeholder="One sentence on why this step is required" data-testid="tt-edit-why" /></div>
          <div><label className="text-xs font-medium text-slate-500">Phase</label><Input value={f.phase} onChange={(e) => up("phase", e.target.value)} data-testid="tt-edit-phase" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-slate-500">Assigned role</label>
              <Select value={f.assigned_role} onValueChange={(v) => up("assigned_role", v)}><SelectTrigger data-testid="tt-edit-role"><SelectValue /></SelectTrigger><SelectContent className="bg-white">{(meta?.roles || []).map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>)}</SelectContent></Select></div>
            <div><label className="text-xs font-medium text-slate-500">State</label>
              <Select value={f.state} onValueChange={(v) => up("state", v)}><SelectTrigger data-testid="tt-edit-state"><SelectValue /></SelectTrigger><SelectContent className="bg-white">{(meta?.states || ["all"]).map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All" : s}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-slate-500">Trigger</label>
              <Select value={f.trigger} onValueChange={(v) => up("trigger", v)}><SelectTrigger data-testid="tt-edit-trigger"><SelectValue /></SelectTrigger><SelectContent className="bg-white">{(meta?.triggers || []).map((t) => <SelectItem key={t} value={t}>{TRIGGER_LABEL[t] || t}</SelectItem>)}</SelectContent></Select></div>
            <div><label className="text-xs font-medium text-slate-500">Due timing</label>
              <Select value={f.offset_type} onValueChange={(v) => up("offset_type", v)}><SelectTrigger data-testid="tt-edit-offset"><SelectValue /></SelectTrigger><SelectContent className="bg-white">{OFFSET_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
          </div>
          {f.trigger === "after_task" && (
            <div><label className="text-xs font-medium text-slate-500">Activates after this task is completed</label>
              <Select value={f.after_item_key} onValueChange={(v) => up("after_item_key", v)}><SelectTrigger data-testid="tt-edit-after-task"><SelectValue placeholder="Choose prerequisite task" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">{siblings.map((s) => <SelectItem key={s.item_key} value={s.item_key}>{s.phase} · {s.title}</SelectItem>)}</SelectContent></Select></div>
          )}
          {(f.offset_type === "days" || f.offset_type === "days_before_career") && (
            <div><label className="text-xs font-medium text-slate-500">Days</label><Input type="number" value={f.offset_days} onChange={(e) => up("offset_days", e.target.value)} data-testid="tt-edit-days" /></div>
          )}
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.required} onCheckedChange={(v) => up("required", v)} data-testid="tt-edit-required" /> Required</label>
            <select value={f.classification} onChange={(e) => up("classification", e.target.value)} data-testid="tt-edit-classification" className="text-sm border border-slate-200 rounded-md px-2 py-1">
              <option value="">Classification: auto (from Required)</option>
              <option value="official">Official requirement</option><option value="required">Required operational</option>
              <option value="recommended">Recommended development</option><option value="conditional">Conditional</option>
              <option value="post_career">Post-Career</option><option value="optional">Optional development</option>
            </select>
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.candidate_visible} onCheckedChange={(v) => up("candidate_visible", v)} data-testid="tt-edit-visible" /> Candidate visible</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => up("active", v)} data-testid="tt-edit-active" /> Active</label>
          </div>
          <div><label className="text-xs font-medium text-slate-500">Candidate instructions (shown in portal)</label><RichTextEditor value={f.candidate_instructions} onChange={(v) => up("candidate_instructions", v)} minHeight={110} testId="tt-edit-instructions" /></div>
          <TemplateResources value={f.resources} onChange={(v) => up("resources", v)} />
          <TemplateSubmission value={f.submission} onChange={(v) => up("submission", v)} />
          <div><label className="text-xs font-medium text-slate-500">Internal notes (never shown to candidate)</label><RichTextEditor value={f.internal_notes} onChange={(v) => up("internal_notes", v)} minHeight={90} testId="tt-edit-notes" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} data-testid="tt-edit-save" className="bg-[#006090] hover:bg-[#004d73]">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApplyDialog({ program, subtype, orgParam, onClose }) {
  const [enr, setEnr] = useState([]);
  const [sel, setSel] = useState({});
  const [inclUnres, setInclUnres] = useState(false);
  const [inclPast, setInclPast] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/program-tasks/enrollments", { params: { program, subtype, organization_id: orgParam } })
      .then((r) => setEnr(r.data.enrollments || [])).catch((e) => toast.error(apiError(e)));
  }, [program, subtype, orgParam]);

  const selectedIds = Object.keys(sel).filter((k) => sel[k]);
  const allChecked = enr.length > 0 && selectedIds.length === enr.length;
  const toggleAll = () => { const n = {}; if (!allChecked) enr.forEach((e) => (n[e.id] = true)); setSel(n); setPreview(null); };
  const body = () => ({ enrollment_ids: selectedIds, include_unresolved: inclUnres, include_past_phases: inclPast });

  const runPreview = async () => {
    if (selectedIds.length === 0) return toast.error("Select at least one enrollment");
    setBusy(true);
    try { const r = await api.post("/program-tasks/apply/preview", body()); setPreview(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const runApply = async () => {
    setBusy(true);
    try { const r = await api.post("/program-tasks/apply", body()); toast.success(`Applied — ${r.data.tasks_created} task(s) created`); onClose(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const stat = (label, val, testid, tone = "slate") => (
    <div className="rounded-lg border border-slate-200 p-2.5 text-center"><div className={`text-lg font-bold ${tone === "green" ? "text-emerald-600" : tone === "amber" ? "text-amber-600" : "text-slate-700"}`} data-testid={testid}>{val}</div><div className="text-[10px] text-slate-500">{label}</div></div>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="tt-apply-dialog">
        <DialogHeader><DialogTitle>Apply Task Templates to Existing Enrollments</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Generates candidate/internal tasks from the effective templates. Safe & idempotent — never changes the program, version, current phase, gating, or completed tasks.</p>

        <div className="rounded-lg border border-slate-200 mt-2">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50 text-xs font-medium text-slate-600">
            <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="tt-apply-selectall" /> Select all ({enr.length})
          </div>
          <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
            {enr.length === 0 ? <p className="p-3 text-sm text-slate-400">No active enrollments for this program/org.</p> : enr.map((e) => (
              <label key={e.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer" data-testid={`tt-apply-enr-${e.id}`}>
                <Checkbox checked={!!sel[e.id]} onCheckedChange={(v) => { setSel((p) => ({ ...p, [e.id]: v })); setPreview(null); }} />
                <span className="flex-1">{e.person_name}</span>
                <span className="text-[11px] text-slate-400">{e.program}{e.subtype ? `·${e.subtype}` : ""} · {e.state || "—"}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-5 mt-2">
          <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={inclUnres} onCheckedChange={(v) => { setInclUnres(v); setPreview(null); }} data-testid="tt-apply-incl-unresolved" /> Include unresolved-phase tasks</label>
          <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={inclPast} onCheckedChange={(v) => { setInclPast(v); setPreview(null); }} data-testid="tt-apply-incl-past" /> Include past-phase tasks</label>
        </div>

        {preview && (
          <div className="mt-2" data-testid="tt-apply-preview">
            <div className="grid grid-cols-4 gap-2">
              {stat("Enrollments", preview.enrollments_selected, "tt-prev-enrollments")}
              {stat("To create", preview.tasks_to_create, "tt-prev-create", "green")}
              {stat("Already present", preview.skipped_existing, "tt-prev-existing")}
              {stat("Completed", preview.skipped_completed, "tt-prev-completed")}
              {stat("Past phase", preview.skipped_past_phase, "tt-prev-past")}
              {stat("State n/a", preview.skipped_state, "tt-prev-state")}
              {stat("Unresolved", preview.unresolved_mapping, "tt-prev-unresolved", "amber")}
              {stat("Errors", (preview.errors || []).length, "tt-prev-errors")}
            </div>
            {preview.unresolved_mapping > 0 && (
              <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-[11px] text-amber-800" data-testid="tt-prev-unresolved-detail">
                <strong>Phase mapping unresolved</strong> (skipped by default — enable "Include unresolved-phase tasks" to add them):
                {Object.entries(preview.unresolved_samples || {}).map(([ph, titles]) => (
                  <div key={ph} className="mt-1"><span className="font-medium">{ph}:</span> {titles.join(", ")}</div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="outline" onClick={runPreview} disabled={busy} data-testid="tt-apply-preview-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Preview"}</Button>
          <Button onClick={runApply} disabled={busy || !preview || preview.tasks_to_create === 0} data-testid="tt-apply-confirm-btn" className="bg-[#006090] hover:bg-[#004d73]">Apply {preview ? `${preview.tasks_to_create} task(s)` : ""}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
